class LookbookHotspot extends HTMLElement {
  static get observedAttributes() {
    return ['x', 'y'];
  }

  get x() {
    return Number(this.getAttribute('x') ?? '0');
  }

  get y() {
    return Number(this.getAttribute('y') ?? '0');
  }

  attributeChangedCallback() {
    this.#notifyChange();
  }

  connectedCallback() {
    this.style.position ||= 'absolute';
    this.#notifyChange();
  }

  #notifyChange() {
    this.dispatchEvent(new CustomEvent('lookbook-hotspot-change', { bubbles: true }));
  }
}

class LookbookScene extends HTMLElement {
  #img;
  #resizeObs;
  #pending = false;

  connectedCallback() {
    this.#img = this.querySelector('img');
    if (!this.style.position) this.style.position = 'relative';

    // Update when the image loads / resizes / children change
    if (this.#img) {
      if (this.#img.complete) this.#update();
      this.#img.addEventListener('load', () => this.#update());
    }

    this.#resizeObs = new ResizeObserver(() => this.#update());
    this.#resizeObs.observe(this);

    // Reposition when a hotspot's x/y changes
    this.addEventListener('lookbook-hotspot-change', () => this.#update());

    new MutationObserver(() => {
      if (!this.#img) this.#img = this.querySelector('img');
      this.#update();
    }).observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['x', 'y'] });
  }

  disconnectedCallback() {
    this.#resizeObs?.disconnect();
  }

  // Core math: mirror object-fit: cover with object-position offsets
  #update() {
    if (this.#pending) return;
    this.#pending = true;
    queueMicrotask(() => {
      // coalesce multiple triggers in a frame
      this.#pending = false;
      const img = this.#img;
      if (!img) return;

      const nw = img.naturalWidth,
        nh = img.naturalHeight;
      const cw = this.clientWidth,
        ch = this.clientHeight;
      if (!nw || !nh || !cw || !ch) return;

      const scale = Math.max(cw / nw, ch / nh);
      const dispW = nw * scale,
        dispH = nh * scale;

      const { xPct, yPct } = this.#parseObjectPosition(img);
      const offsetX = (dispW - cw) * (xPct / 100);
      const offsetY = (dispH - ch) * (yPct / 100);

      this.querySelectorAll('lookbook-hotspot').forEach((hs) => {
        const xPctHs = Number(hs.getAttribute('x') ?? '0');
        const yPctHs = Number(hs.getAttribute('y') ?? '0');

        // Convert % of natural image -> px in container space
        const xPx = (xPctHs / 100) * nw * scale - offsetX;
        const yPx = (yPctHs / 100) * nh * scale - offsetY;

        // Place it
        const el = hs;
        el.style.left = `${xPx}px`;
        el.style.top = `${yPx}px`;
        el.style.position ||= 'absolute';
      });
    });
  }

  #parseObjectPosition(img) {
    const { objectPosition } = getComputedStyle(img);
    // Normalise keywords/percentages -> {xPct, yPct}
    const mapKeyword = (k) => ({ left: 0, top: 0, center: 50, right: 100, bottom: 100 })[k] ?? 50;
    const parts = objectPosition.trim().split(/\s+/);
    let [ax, ay] = parts.length === 2 ? parts : ['50%', '50%'];
    const toPct = (v) => (v.endsWith('%') ? parseFloat(v) : mapKeyword(v));
    return { xPct: toPct(ax), yPct: toPct(ay) };
  }
}

customElements.define('lookbook-scene', LookbookScene);
customElements.define('lookbook-hotspot', LookbookHotspot);
